import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm, fmt, fmtDate } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Banknote, Loader2, Printer, Trash2, Pencil, Search, CalendarDays, Wallet, Users, RefreshCw } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import "./payslip.css";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (m) => { const [y, mo] = m.split("-"); return `${MONTHS[parseInt(mo, 10) - 1]} ${y}`; };
const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

function sessionMonthOptions(month) {
  const y = parseInt(month.split("-")[0], 10), m = parseInt(month.split("-")[1], 10);
  const start = m >= 4 ? y : y - 1;
  const opts = [];
  for (let i = 0; i < 12; i++) { const mo = ((3 + i) % 12) + 1; const yr = mo >= 4 ? start : start + 1; opts.push(`${yr}-${String(mo).padStart(2, "0")}`); }
  return opts;
}

function PaySalaryDialog({ open, onOpenChange, month, row, workingDays, salaryFormula, onSaved }) {
  const editing = row?.payment;
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [att, setAtt] = useState(null);
  const [dueAdv, setDueAdv] = useState(null);

  useEffect(() => {
    if (!open || !row) return;
    const p = row.payment;
    setF({
      basic: String(p ? p.basic_salary : row.basic || 0),
      allow: String(p ? p.allowances || 0 : 0),
      paidLeave: String(p ? (p.paid_leave || 0) : 1),
      leaveDays: String(p ? (p.leave_days ?? row.leave) : row.leave || 0),
      lateDays: String(p ? (p.late_days ?? row.late ?? 0) : row.late || 0),
      halfDays: String(p ? (p.half_days ?? row.half ?? 0) : row.half || 0),
      absentDays: String(p ? (p.absent_days ?? row.absent ?? 0) : row.absent || 0),
      pfDed: String(p ? p.pf_deduction || 0 : 0),
      advance: String(p ? p.advance || 0 : 0),
      mode: p ? p.mode : "Bank Transfer",
      date: p ? p.payment_date : new Date().toISOString().slice(0, 10),
      remarks: p ? p.remarks || "" : "",
    });
    api.get("/payroll/attendance-summary", { params: { staff_id: row.staff_id, month } }).then((r) => setAtt(r.data)).catch(() => setAtt(null));
    if (!p) {
      api.get("/advances/due", { params: { staff_id: row.staff_id, month } }).then((r) => { setDueAdv(r.data); if (r.data.total) setF((prev) => ({ ...prev, advance: String(r.data.total) })); }).catch(() => setDueAdv(null));
    } else setDueAdv(null);
  }, [open, row, month]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const num = (k) => Number(f[k]) || 0;
  const wd = Number(workingDays) || 30;
  const perDay = num("basic") / wd;
  const formula = (salaryFormula && salaryFormula.length) ? salaryFormula : [{ sign: "+", status: "Present" }, { sign: "+", status: "Late" }, { sign: "+", status: "Half Day" }, { sign: "+", status: "Holiday" }, { sign: "+", status: "Paid Leave" }];
  const signMap = {}; formula.forEach((t) => { signMap[t.status] = t.sign; });
  const statusCounts = { Present: row?.present || 0, Late: num("lateDays"), "Half Day": num("halfDays"), Holiday: row?.holiday || 0, "Paid Leave": num("paidLeave"), Leave: num("leaveDays"), Absent: num("absentDays") };
  let payableRaw = 0;
  Object.entries(statusCounts).forEach(([st, cnt]) => {
    const w = st === "Half Day" ? 0.5 : 1;
    const v = cnt * w;
    if (signMap[st] === "-") payableRaw -= v;
    else if (signMap[st] === "+") payableRaw += v;
  });
  const payableDays = Math.max(0, Math.min(payableRaw, wd));
  const gross = Math.round(num("basic") * payableDays / wd);
  const leaveDed = num("basic") - gross;
  const chargeableDays = Math.round((wd - payableDays) * 10) / 10;
  const net = gross + num("allow") - num("pfDed") - num("advance");

  const save = async () => {
    setSaving(true);
    try {
      const body = { staff_id: row.staff_id, month, basic_salary: num("basic"), allowances: num("allow"), leave_days: num("leaveDays"), late_days: num("lateDays"), half_days: num("halfDays"), absent_days: num("absentDays"), paid_leave: num("paidLeave"), leave_deduction: leaveDed, pf_deduction: num("pfDed"), advance: num("advance"), payment_date: f.date, mode: f.mode, remarks: f.remarks };
      if (editing) await api.put(`/payroll/${row.payment.id}`, body);
      else await api.post("/payroll", body);
      toast.success(editing ? "Salary updated" : "Salary recorded");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="pay-salary-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit" : "Pay"} Salary — {row?.name} · {monthLabel(month)}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Basic (₹)</Label><Input type="number" value={f.basic || ""} onChange={(e) => set("basic", e.target.value)} className="mt-1" data-testid="pay-basic" /></div>
            <div><Label>Allowance</Label><Input type="number" value={f.allow || ""} onChange={(e) => set("allow", e.target.value)} className="mt-1" data-testid="pay-allow" /></div>
            <div><Label>Advance</Label><Input type="number" value={f.advance || ""} onChange={(e) => set("advance", e.target.value)} className="mt-1" data-testid="pay-advance" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Leave (days)</Label><Input type="number" value={f.leaveDays || ""} onChange={(e) => set("leaveDays", e.target.value)} className="mt-1" data-testid="pay-leave" /></div>
            <div><Label>Late (days)</Label><Input type="number" value={f.lateDays || ""} onChange={(e) => set("lateDays", e.target.value)} className="mt-1" data-testid="pay-late" /></div>
            <div><Label>Absent (days)</Label><Input type="number" value={f.absentDays || ""} onChange={(e) => set("absentDays", e.target.value)} className="mt-1" data-testid="pay-absent" /></div>
            <div><Label>Half Days</Label><Input type="number" value={f.halfDays || ""} onChange={(e) => set("halfDays", e.target.value)} className="mt-1" data-testid="pay-half" /></div>
            <div><Label>Paid Leave</Label><Input type="number" value={f.paidLeave ?? ""} onChange={(e) => set("paidLeave", e.target.value)} className="mt-1" data-testid="pay-paidleave" /></div>
            <div><Label>PF Deduction</Label><Input type="number" value={f.pfDed || ""} onChange={(e) => set("pfDed", e.target.value)} className="mt-1" data-testid="pay-pf" /></div>
          </div>
          <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 flex items-center justify-between gap-3" data-testid="leave-ded-auto">
            <span className="text-slate-600">Per-day <b>{fmt(Math.round(perDay))}</b> · Paid leave <b>{num("paidLeave")}</b> · Deducted days <b>{chargeableDays}</b> · Payable days <b>{Math.max(0, Math.round(((Number(workingDays) || 30) - chargeableDays) * 10) / 10)}</b></span>
            <span className="text-rose-600 font-semibold shrink-0" data-testid="pay-leaveded">− {fmt(leaveDed)}</span>
          </div>
          {dueAdv?.total > 0 && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-700" data-testid="adv-due-note">
              Advance due this month auto-added to the Advance deduction: <b>{fmt(dueAdv.total)}</b> ({dueAdv.items.length} entr{dueAdv.items.length > 1 ? "ies" : "y"}). Edit the Advance field if you want to deduct a different amount.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Payment Date</Label><Input type="date" value={f.date || ""} onChange={(e) => set("date", e.target.value)} className="mt-1" /></div>
            <div><Label>Mode</Label>
              <Select value={f.mode} onValueChange={(v) => set("mode", v)}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{["Bank Transfer", "Cash", "Cheque", "UPI"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div><Label>Remarks</Label><Input value={f.remarks || ""} onChange={(e) => set("remarks", e.target.value)} className="mt-1" placeholder="Optional" /></div>
          <div className="flex justify-between items-center bg-indigo-50 rounded-lg px-4 py-2.5">
            <span className="text-sm font-medium text-slate-600">Net Payable</span>
            <span className="text-lg font-bold text-indigo-700" data-testid="pay-net">{fmt(net)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-payroll-btn">{saving ? <Loader2 size={16} className="animate-spin mr-1" /> : null}{editing ? "Update" : "Record Payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Payslip({ data }) {
  const p = data.payment, s = data.staff, school = data.school;
  const row = (k, v) => <div className="flex justify-between py-1.5 border-b border-slate-100"><span className="text-slate-500">{k}</span><span className="font-medium">{v}</span></div>;
  const totalDed = (p.leave_deduction || 0) + (p.pf_deduction || 0) + (p.advance || 0) + (p.deductions || 0);
  return (
    <div className="report-card bg-white p-8 text-slate-800" data-testid="payslip-view">
      <div className="text-center border-b-2 border-slate-800 pb-3 mb-4 flex items-center justify-center gap-3">
        {school?.logo && <img src={school.logo} alt="logo" className="w-14 h-14 object-contain" />}
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight">{school?.name || "SSSPS"}</h2>
          <p className="text-sm text-slate-500">Salary Payslip — {monthLabel(p.month)}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-8 text-sm mb-4">
        <div>{row("Name", s.name)}{row("Designation", s.designation || "—")}{row("Role", s.role)}</div>
        <div>{row("Payment Date", fmtDate(p.payment_date))}{row("Mode", p.mode)}{row("Payslip No.", p.id.slice(-8).toUpperCase())}</div>
      </div>
      <table className="w-full text-sm border border-slate-300 border-collapse mb-4">
        <thead className="bg-slate-100"><tr><th className="border border-slate-300 px-3 py-2 text-left">Earnings</th><th className="border border-slate-300 px-3 py-2 text-right">Amount</th><th className="border border-slate-300 px-3 py-2 text-left">Deductions</th><th className="border border-slate-300 px-3 py-2 text-right">Amount</th></tr></thead>
        <tbody>
          <tr><td className="border border-slate-300 px-3 py-2">Basic Salary</td><td className="border border-slate-300 px-3 py-2 text-right">{fmt(p.basic_salary)}</td><td className="border border-slate-300 px-3 py-2">Leave Deduction</td><td className="border border-slate-300 px-3 py-2 text-right">{fmt(p.leave_deduction || 0)}</td></tr>
          <tr><td className="border border-slate-300 px-3 py-2">Allowances</td><td className="border border-slate-300 px-3 py-2 text-right">{fmt(p.allowances)}</td><td className="border border-slate-300 px-3 py-2">PF Deduction</td><td className="border border-slate-300 px-3 py-2 text-right">{fmt(p.pf_deduction || 0)}</td></tr>
          <tr><td className="border border-slate-300 px-3 py-2"></td><td className="border border-slate-300 px-3 py-2"></td><td className="border border-slate-300 px-3 py-2">Advance</td><td className="border border-slate-300 px-3 py-2 text-right">{fmt(p.advance || 0)}</td></tr>
        </tbody>
        <tfoot><tr className="bg-slate-50 font-semibold"><td className="border border-slate-300 px-3 py-2">Gross</td><td className="border border-slate-300 px-3 py-2 text-right">{fmt(p.basic_salary + p.allowances)}</td><td className="border border-slate-300 px-3 py-2">Total Deductions</td><td className="border border-slate-300 px-3 py-2 text-right">{fmt(totalDed)}</td></tr></tfoot>
      </table>
      <div className="flex justify-between items-center bg-emerald-50 rounded-lg px-4 py-3">
        <span className="font-semibold text-slate-700">Net Salary Paid</span>
        <span className="text-xl font-extrabold text-emerald-700">{fmt(p.net_amount)}</span>
      </div>
      {p.remarks && <p className="text-xs text-slate-400 mt-3">Remarks: {p.remarks}</p>}
      <div className="flex justify-between mt-12 text-sm text-slate-500">
        <div className="text-center"><div className="border-t border-slate-400 pt-1 px-6">Accountant</div></div>
        <div className="text-center"><div className="border-t border-slate-400 pt-1 px-6">Principal</div></div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><Icon size={20} /></div>
      <div className="min-w-0">
        <div className="text-xs text-slate-400 truncate">{label}</div>
        <div className="text-lg font-heading font-extrabold text-slate-900 tabular">{value}</div>
        {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
      </div>
    </div>
  );
}

function AdvanceDialog({ open, onOpenChange, roster, onSaved }) {
  const now = new Date();
  const nextM = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const defMonth = `${nextM.getFullYear()}-${String(nextM.getMonth() + 1).padStart(2, "0")}`;
  const [f, setF] = useState({ staff_id: "", amount: "", date: new Date().toISOString().slice(0, 10), reason: "", deduct_month: defMonth });
  const [list, setList] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/advances").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { if (open) { load(); setF((p) => ({ ...p, staff_id: "", amount: "", reason: "" })); } }, [open]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.staff_id) { toast.error("Select a staff member"); return; }
    if (!Number(f.amount)) { toast.error("Enter an amount"); return; }
    if (!f.deduct_month) { toast.error("Choose a deduction month"); return; }
    setSaving(true);
    try {
      await api.post("/advances", { staff_id: f.staff_id, amount: Number(f.amount), date: f.date, reason: f.reason, deduct_month: f.deduct_month });
      toast.success("Advance recorded");
      load(); onSaved?.();
      setF((p) => ({ ...p, amount: "", reason: "" }));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete this advance?")) return; try { await api.delete(`/advances/${id}`); toast.success("Deleted"); load(); onSaved?.(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="advance-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Wallet size={18} className="text-indigo-500" />Advance Register</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Log a salary advance and pick the month it should be recovered. The chosen month's salary will auto-deduct it.</p>
        <div className="grid sm:grid-cols-6 gap-2 items-end bg-slate-50 border border-slate-200 rounded-lg p-3">
          <div className="sm:col-span-2"><Label>Staff</Label>
            <Select value={f.staff_id} onValueChange={(v) => set("staff_id", v)}><SelectTrigger className="mt-1" data-testid="adv-staff"><SelectValue placeholder="Select staff" /></SelectTrigger>
              <SelectContent>{roster.map((r) => <SelectItem key={r.staff_id} value={r.staff_id}>{r.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Amount (₹)</Label><Input type="number" value={f.amount} onChange={(e) => set("amount", e.target.value)} className="mt-1" data-testid="adv-amount" /></div>
          <div><Label>Given On</Label><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className="mt-1" data-testid="adv-date" /></div>
          <div><Label>Deduct In</Label><Input type="month" value={f.deduct_month} onChange={(e) => set("deduct_month", e.target.value)} className="mt-1" data-testid="adv-month" /></div>
          <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="adv-save-btn">{saving ? <Loader2 size={16} className="animate-spin" /> : <Banknote size={16} />}Add</Button>
          <div className="sm:col-span-6"><Label>Reason</Label><Input value={f.reason} onChange={(e) => set("reason", e.target.value)} className="mt-1" placeholder="Optional" data-testid="adv-reason" /></div>
        </div>
        <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>{["Staff", "Amount", "Given On", "Deduct In", "Status", "Reason", ""].map((h) => <th key={h} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {list.length === 0 ? <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No advances recorded.</td></tr> :
                list.map((a) => (
                  <tr key={a.id} data-testid={`adv-row-${a.id}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{a.staff_name}</td>
                    <td className="px-3 py-2 tabular text-rose-600">{fmt(a.amount)}</td>
                    <td className="px-3 py-2 text-slate-500">{fmtDate(a.date)}</td>
                    <td className="px-3 py-2 text-slate-600">{monthLabel(a.deduct_month)}</td>
                    <td className="px-3 py-2">{a.status === "Deducted" ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold" data-testid={`adv-status-${a.id}`}>Deducted</span> : <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold" data-testid={`adv-status-${a.id}`}>Pending</span>}</td>
                    <td className="px-3 py-2 text-slate-500 max-w-[160px] truncate">{a.reason || "—"}</td>
                    <td className="px-3 py-2">{a.status !== "Deducted" && <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => del(a.id)} data-testid={`adv-del-${a.id}`}><Trash2 size={14} /></Button>}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Payroll() {
  const { user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [roster, setRoster] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [countLateAsLeave, setCountLateAsLeave] = useState(false);
  const [latePer, setLatePer] = useState("4");
  const [leavePer, setLeavePer] = useState("1");
  const [dialogRow, setDialogRow] = useState(null);
  const [slip, setSlip] = useState(null);
  const [workingDays, setWorkingDays] = useState(30);
  const [salaryFormula, setSalaryFormula] = useState(null);
  const [bulkPaying, setBulkPaying] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [advOpen, setAdvOpen] = useState(false);
  const canEdit = hasPerm(user, "edit_staff");
  const canDelete = hasPerm(user, "delete");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/payroll/roster", { params: { month } }).then((r) => setRoster(r.data)),
      api.get("/payroll/summary", { params: { month } }).then((r) => setSummary(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, [month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/settings").then((r) => { const s = r.data || {}; setWorkingDays(s.working_days || 30); setCountLateAsLeave(!!s.count_late_as_leave); if (s.late_per != null) setLatePer(String(s.late_per)); if (s.leave_per != null) setLeavePer(String(s.leave_per)); setSalaryFormula(s.salary_formula || null); }).catch(() => {}); }, []);

  const openSlip = async (id) => { try { const r = await api.get(`/payroll/${id}/payslip`); setSlip(r.data); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const del = async (id) => { if (!window.confirm("Delete this salary payment?")) return; try { await api.delete(`/payroll/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const refreshAttendance = async () => {
    if (!selectedStaff.length) { toast.info("Select staff to refresh"); return; }
    if (!window.confirm(`Refresh attendance for ${selectedStaff.length} selected staff for ${monthLabel(month)}? Any recorded payroll for them this month will be removed and re-read from the Staff Attendance Register.`)) return;
    setRefreshing(true);
    try {
      const r = await api.post("/payroll/refresh-attendance", { staff_ids: selectedStaff, month });
      toast.success(`Refreshed ${r.data.refreshed} staff from register`);
      setSelectedStaff([]);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };

  const pendingCount = roster.filter((r) => !r.payment).length;
  const bulkPay = async () => {
    if (!pendingCount) { toast.info("No pending staff to pay"); return; }
    if (!window.confirm(`Record salary for ${pendingCount} unpaid staff member(s) for ${monthLabel(month)}? Leaves & late will be auto-deducted.`)) return;
    setBulkPaying(true);
    try {
      const r = await api.post("/payroll/bulk-pay", { month, working_days: workingDays, late_as_leave: countLateAsLeave, late_per: Number(latePer) || 4, leave_per: Number(leavePer) || 1, paid_leave: 1 });
      toast.success(`Paid ${r.data.paid} staff · ${fmt(r.data.total)}`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBulkPaying(false); }
  };

  const filtered = roster.filter((r) => {    const status = r.payment ? "paid" : "pending";
    if (statusFilter !== "all" && statusFilter !== status) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (![r.name, r.father_name, r.designation].join(" ").toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const toggleStaff = (id) => setSelectedStaff((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const allRosterSelected = filtered.length > 0 && filtered.every((r) => selectedStaff.includes(r.staff_id));
  const toggleAllStaff = () => setSelectedStaff(allRosterSelected ? [] : filtered.map((r) => r.staff_id));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><Banknote className="text-indigo-500" size={26} />Staff Payroll</h1>
          <p className="text-slate-500">Pay salaries, track leaves & deductions, and generate payslips.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={refreshAttendance} disabled={refreshing} className="gap-1.5" data-testid="refresh-attendance-btn"><RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />Refresh Attendance{selectedStaff.length ? ` (${selectedStaff.length})` : ""}</Button>
            <Button variant="outline" onClick={() => setAdvOpen(true)} className="gap-1.5" data-testid="advance-register-btn"><Wallet size={16} />Advance Register</Button>
            <Button onClick={bulkPay} disabled={bulkPaying || pendingCount === 0} className="gap-1.5" data-testid="bulk-pay-btn">
              {bulkPaying ? <Loader2 size={16} className="animate-spin" /> : <Banknote size={16} />}
              Pay All Pending{pendingCount ? ` (${pendingCount})` : ""}
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Wallet} label={`Total Payout Since April`} value={fmt(summary?.since_april || 0)} sub={summary ? `from ${monthLabel(summary.since_label)}` : ""} testid="stat-since-april" />
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3" data-testid="stat-month-total">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><CalendarDays size={20} /></div>
          <div className="min-w-0 flex-1">
            <div className="text-xs text-slate-400">Total Payout</div>
            <div className="text-lg font-heading font-extrabold text-slate-900 tabular">{fmt(summary?.month_total || 0)}</div>
            <Select value={month} onValueChange={setMonth}><SelectTrigger className="h-7 mt-1 text-xs" data-testid="payroll-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{sessionMonthOptions(month).map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        <StatCard icon={Users} label="Paid Staff" value={`${summary?.paid_count || 0} / ${summary?.total_staff || 0}`} sub="paid / total" testid="stat-paid-staff" />
        <StatCard icon={Banknote} label="Advance (this month)" value={fmt(summary?.advance || 0)} testid="stat-advance" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search staff name, father or designation…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="payroll-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="payroll-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="pending">Pending</SelectItem></SelectContent>
        </Select>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="payroll-month" />
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <Checkbox checked={countLateAsLeave} onCheckedChange={(v) => setCountLateAsLeave(!!v)} data-testid="count-late-toggle" />
          Count Late as Leave
        </label>
        {countLateAsLeave && (
          <div className="flex items-center gap-1.5 text-sm text-slate-500" data-testid="late-ratio">
            <Input type="number" value={latePer} onChange={(e) => setLatePer(e.target.value)} className="w-16 h-9" data-testid="late-per" /> late
            <span>=</span>
            <Input type="number" value={leavePer} onChange={(e) => setLeavePer(e.target.value)} className="w-16 h-9" data-testid="leave-per" /> leave
          </div>
        )}
        <span className="text-xs text-slate-400 ml-auto">{filtered.length} of {roster.length}</span>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 table-scroll">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          filtered.length === 0 ? <div className="py-16 text-center text-slate-400"><Banknote size={40} className="mx-auto mb-2 opacity-40" />No staff match.</div> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr><th className="px-3 py-2.5"><Checkbox checked={allRosterSelected} onCheckedChange={toggleAllStaff} data-testid="payroll-select-all" /></th>{["Se.", "Photo", "Staff", "Basic Salary", "Present", "Leave", "Late", "Late Leave", "Absent", "Half", "Holiday", "Payable Days", "Paid Leave", "Allowance", "Leave Ded.", "PF Ded.", "Salary", "Status", "Action"].map((h) => <th key={h} className="px-3 py-2.5 text-left whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((r, i) => {
                  const p = r.payment;
                  const wd = Number(workingDays) || 30;
                  const _fm = (salaryFormula && salaryFormula.length) ? salaryFormula : [{ sign: "+", status: "Present" }, { sign: "+", status: "Late" }, { sign: "+", status: "Half Day" }, { sign: "+", status: "Holiday" }, { sign: "+", status: "Paid Leave" }];
                  const _sign = {}; _fm.forEach((t) => { _sign[t.status] = t.sign; });
                  const cLeave = p ? (p.leave_days ?? r.leave) : r.leave;
                  const cLate = p ? (p.late_days ?? r.late ?? 0) : (r.late || 0);
                  const cHalf = p ? (p.half_days ?? r.half ?? 0) : (r.half || 0);
                  const cAbsent = p ? (p.absent_days ?? r.absent ?? 0) : (r.absent || 0);
                  const _base = { Present: r.present || 0, Late: cLate, "Half Day": cHalf, Holiday: r.holiday || 0, Leave: cLeave, Absent: cAbsent };
                  let _bpd = 0; Object.entries(_base).forEach(([st, c]) => { const w = st === "Half Day" ? 0.5 : 1; const v = c * w; if (_sign[st] === "-") _bpd -= v; else if (_sign[st] === "+") _bpd += v; });
                  const cPaidLeave = p ? (p.paid_leave ?? 0) : (_bpd > 15 ? 1 : 0);
                  const _counts = { ..._base, "Paid Leave": cPaidLeave };
                  let _pd = 0; Object.entries(_counts).forEach(([st, c]) => { const w = st === "Half Day" ? 0.5 : 1; const v = c * w; if (_sign[st] === "-") _pd -= v; else if (_sign[st] === "+") _pd += v; });
                  const _latePer = Number(latePer) || 4;
                  const _leavePer = Number(leavePer) || 1;
                  const extraLeave = (countLateAsLeave && _latePer > 0) ? Math.floor(cLate / _latePer) * _leavePer : 0;
                  const payableDays = Math.max(0, (Math.min(Math.round(_pd * 10) / 10, wd)) - extraLeave);
                  const gross = Math.round(r.basic * payableDays / wd);
                  const penDed = r.basic - gross;
                  return (
                    <tr key={r.staff_id} className="hover:bg-slate-50" data-testid={`payroll-row-${r.staff_id}`}>
                      <td className="px-3 py-2.5"><Checkbox checked={selectedStaff.includes(r.staff_id)} onCheckedChange={() => toggleStaff(r.staff_id)} data-testid={`payroll-check-${r.staff_id}`} /></td>
                      <td className="px-3 py-2.5 text-slate-400 tabular">{i + 1}</td>
                      <td className="px-3 py-2.5">
                        {r.photo ? <img src={r.photo} alt={r.name} className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-semibold">{initials(r.name)}</div>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-slate-800">{r.name}</div>
                        <div className="text-[11px] text-slate-400">{r.father_name ? `S/o ${r.father_name} · ` : ""}{r.designation}</div>
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 tabular">{fmt(p ? p.basic_salary : r.basic)}</td>
                      <td className="px-3 py-2.5 text-emerald-600 font-semibold tabular" data-testid={`present-${r.staff_id}`}>{r.present || 0}</td>
                      <td className="px-3 py-2.5 text-slate-600 tabular">{cLeave}</td>
                      <td className="px-3 py-2.5 text-slate-600 tabular" data-testid={`late-${r.staff_id}`}>{cLate}</td>
                      <td className="px-3 py-2.5 text-amber-600 tabular" data-testid={`late-leave-${r.staff_id}`}>{extraLeave}</td>
                      <td className="px-3 py-2.5 text-rose-600 tabular" data-testid={`absent-${r.staff_id}`}>{cAbsent}</td>
                      <td className="px-3 py-2.5 text-slate-600 tabular" data-testid={`half-${r.staff_id}`}>{cHalf}</td>
                      <td className="px-3 py-2.5 text-violet-600 tabular" data-testid={`holiday-${r.staff_id}`}>{r.holiday || 0}</td>
                      <td className="px-3 py-2.5 font-semibold text-emerald-600 tabular" data-testid={`payable-${r.staff_id}`}>{payableDays}</td>
                      <td className="px-3 py-2.5 text-slate-600 tabular">{cPaidLeave}</td>
                      <td className="px-3 py-2.5 text-emerald-600 tabular">{p ? fmt(p.allowances) : "—"}</td>
                      <td className="px-3 py-2.5 text-rose-500 tabular">{p ? fmt(p.leave_deduction || 0) : (penDed ? `− ${fmt(penDed)}` : "—")}</td>
                      <td className="px-3 py-2.5 text-rose-500 tabular">{p ? fmt(p.pf_deduction || 0) : "—"}</td>
                      <td className="px-3 py-2.5 font-bold text-slate-800 tabular">{p ? fmt(p.net_amount) : fmt(r.basic - penDed)}</td>
                      <td className="px-3 py-2.5">
                        {p ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold" data-testid={`status-${r.staff_id}`}>Paid</span>
                          : <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold" data-testid={`status-${r.staff_id}`}>Pending</span>}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {p ? (
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" className="gap-1 h-8" onClick={() => openSlip(p.id)} data-testid={`payslip-${r.staff_id}`}><Printer size={13} />Slip</Button>
                            {canEdit && <Button size="icon" variant="ghost" className="h-8 w-8 text-indigo-600" onClick={() => setDialogRow(r)} data-testid={`edit-payroll-${r.staff_id}`}><Pencil size={14} /></Button>}
                            {canDelete && <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500" onClick={() => del(p.id)} data-testid={`del-payroll-${r.staff_id}`}><Trash2 size={14} /></Button>}
                          </div>
                        ) : (
                          canEdit && <Button size="sm" className="gap-1 h-8" onClick={() => setDialogRow(r)} data-testid={`pay-${r.staff_id}`}><Banknote size={13} />Pay</Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      </div>

      <PaySalaryDialog open={!!dialogRow} onOpenChange={(o) => !o && setDialogRow(null)} month={month} row={dialogRow} workingDays={workingDays} salaryFormula={salaryFormula} onSaved={load} />
      <AdvanceDialog open={advOpen} onOpenChange={setAdvOpen} roster={roster} onSaved={load} />

      <Dialog open={!!slip} onOpenChange={(o) => !o && setSlip(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0">
          <div className="print-area">{slip && <Payslip data={slip} />}</div>
          <div className="flex justify-end gap-2 p-4 border-t border-slate-100 no-print">
            <Button variant="outline" onClick={() => setSlip(null)}>Close</Button>
            <Button onClick={() => window.print()} className="gap-1.5" data-testid="print-payslip-btn"><Printer size={16} />Print</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
